import * as v from 'valibot';
import { readPath } from './match.ts';

/**
 * Evals for an agent's instructions.
 *
 * Reef's whole argument is that the human gate is *approval of the
 * instructions* rather than approval of every pull request an agent opens. That
 * only holds if changing the instructions is a reviewable change with a test
 * behind it — and until this existed, a skill edit was the one change in the
 * repository that shipped blind: no eval ran, nothing failed, and the first
 * evidence was a bad diff a week later.
 *
 * A case is deliberately *not* a prose comparison. What is asserted is the
 * judgement the skill is supposed to produce — that the validation agent fails
 * an insecure diff, that the CI healer refuses to disable a test, that triage
 * deduplicates. An eval that failed on a reworded conclusion is an eval
 * somebody switches off within a week, which the workflow evals learned first.
 */

/**
 * One assertion about the answer.
 *
 * Four kinds, and each is mechanical. Nothing here asks a model to grade
 * another model: a judge introduces a second thing that can be wrong, and its
 * disagreements are unfalsifiable in exactly the cases that matter.
 */
export const SkillExpectation = v.variant('kind', [
  v.object({
    kind: v.literal('field'),
    /** Dotted path into the structured answer: `verdict.dimensions.0.verdict`. */
    path: v.string(),
    equals: v.unknown(),
  }),
  v.object({
    kind: v.literal('one_of'),
    path: v.string(),
    values: v.array(v.unknown()),
  }),
  /**
   * A phrase the answer has to contain, matched case-insensitively.
   *
   * The narrow exception to "no prose assertions", and it is for refusals: an
   * agent that declines has to *say what it declined*, and there is no
   * structured field for a sentence somebody has to read.
   */
  v.object({
    kind: v.literal('mentions'),
    text: v.string(),
  }),
  /** The opposite, and the more valuable one: the answer must not say this. */
  v.object({
    kind: v.literal('avoids'),
    text: v.string(),
  }),
]);
export type SkillExpectation = v.InferOutput<typeof SkillExpectation>;

export const SkillEvalCase = v.object({
  id: v.pipe(v.string(), v.regex(/^[a-z0-9][a-z0-9-]{2,63}$/)),
  skill: v.string(),
  /** One line on what this case is protecting. Read when it fails, so it says why it exists. */
  intent: v.pipe(v.string(), v.minLength(10)),
  /** What the agent is given: the situation, verbatim, as a step would hand it over. */
  input: v.pipe(v.string(), v.minLength(20)),
  /**
   * The shape the answer must come back in.
   *
   * Required, because every assertion below reads a field out of it — a case
   * with no schema can only assert on prose, which is the thing this format
   * exists to avoid.
   */
  answerShape: v.optional(v.string(), 'a JSON object'),
  expect: v.pipe(v.array(SkillExpectation), v.minLength(1)),
});
export type SkillEvalCase = v.InferOutput<typeof SkillEvalCase>;

export const SkillEvalOutcome = v.picklist(['passed', 'failed', 'skipped']);
export type SkillEvalOutcome = v.InferOutput<typeof SkillEvalOutcome>;

export const SkillEvalResult = v.object({
  id: v.string(),
  skill: v.string(),
  caseId: v.string(),
  outcome: SkillEvalOutcome,
  model: v.nullable(v.string()),
  /** The assertions that failed, in the words the case wrote them. */
  failures: v.optional(v.array(v.string()), []),
  durationMs: v.optional(v.number(), 0),
  ranAt: v.string(),
  ranBy: v.nullable(v.string()),
});
export type SkillEvalResult = v.InferOutput<typeof SkillEvalResult>;

/**
 * Grade one answer against one case.
 *
 * Returns the failures rather than a boolean, and each one quotes what it
 * wanted and what it got: "expected verdict.approved to be false, got true" is
 * a finding somebody acts on, and "case failed" is a finding somebody reruns.
 */
export function gradeAnswer(
  testCase: SkillEvalCase,
  answer: { text: string; structured: unknown },
): string[] {
  const failures: string[] = [];
  const prose = answer.text.toLowerCase();

  /*
   * The path language is the payload matcher's, not a second one.
   *
   * Trigger conditions already read dotted paths out of arbitrary JSON, with
   * `*` to fan out over a list, and an eval case asserting on a structured
   * answer is asking the same question of the same kind of document. A second
   * dialect here would be one more thing whose edge cases differ from the one
   * people already know.
   */
  const at = (path: string): unknown => readPath(answer.structured, path)[0];

  for (const expectation of testCase.expect) {
    switch (expectation.kind) {
      case 'field': {
        const actual = at(expectation.path);
        if (!same(actual, expectation.equals)) {
          failures.push(
            `expected ${expectation.path} to be ${JSON.stringify(expectation.equals)}, got ${JSON.stringify(actual)}`,
          );
        }
        break;
      }

      case 'one_of': {
        const actual = at(expectation.path);
        if (!expectation.values.some((candidate) => same(actual, candidate))) {
          failures.push(
            `expected ${expectation.path} to be one of ${JSON.stringify(expectation.values)}, got ${JSON.stringify(actual)}`,
          );
        }
        break;
      }

      case 'mentions': {
        if (!prose.includes(expectation.text.toLowerCase())) {
          failures.push(`expected the answer to mention "${expectation.text}"`);
        }
        break;
      }

      case 'avoids': {
        if (prose.includes(expectation.text.toLowerCase())) {
          failures.push(`expected the answer not to mention "${expectation.text}"`);
        }
        break;
      }
    }
  }

  return failures;
}

/** Structural equality, shallow enough to be predictable and deep enough to be useful. */
function same(actual: unknown, expected: unknown): boolean {
  if (actual === expected) return true;
  if (typeof actual === 'string' && typeof expected === 'string') {
    return actual.toLowerCase() === expected.toLowerCase();
  }
  return JSON.stringify(actual) === JSON.stringify(expected);
}

/**
 * Skills whose behaviour is load-bearing enough that shipping a version with no
 * case is a mistake rather than a choice.
 *
 * Checked by the corpus validator, which runs in CI with no model provider and
 * therefore no ability to *execute* a case. That is the honest division: CI can
 * prove the cases exist and are well-formed, and only a run with a key can
 * prove they pass.
 */
export const SKILLS_REQUIRING_EVALS = [
  'validation-agent-v1',
  'triage-agent-v1',
  'ci-healing-agent-v1',
  'maintenance-agent-v1',
] as const;
